import { format, formatDistanceToNow, type Locale } from "date-fns";

import { enUS, es, ptBR } from "date-fns/locale";

import { useState } from "react";

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";

import { Card } from "@/components/ui/card";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { parseDate } from "@/lib/parseDate";

import type { DesktopUninstall } from "../api/uninstallsApi";

import { useDesktopUninstallsQuery } from "../hooks/useUninstalls";

import { platformLabelKey } from "./DesktopVersionCell";

import TableTooltipProvider from "./TableTooltipProvider";

const dateLocales: Record<string, Locale> = { es, en: enUS, "pt-BR": ptBR };

// Same closed list as the backend DESKTOP_UNINSTALL_REASONS / extension UNINSTALL_REASONS.
const REASON_KEY: Record<string, string> = {
  not_needed: "UNINSTALLS.reasonNotNeeded",

  too_complex: "UNINSTALLS.reasonTooComplex",

  bugs: "UNINSTALLS.reasonBugs",

  performance: "UNINSTALLS.reasonPerformance",

  missing_features: "UNINSTALLS.reasonMissingFeatures",

  switching: "UNINSTALLS.reasonSwitching",

  other: "UNINSTALLS.reasonOther",
};

const Dash = () => <span className="text-neutral-500">—</span>;

const UninstallsPage = () => {
  const { t, i18n } = useTranslation("common");

  const dateLoc = dateLocales[i18n.language] ?? es;

  const { data, isLoading, isFetching, isError } = useDesktopUninstallsQuery();

  // El aviso cuyo comentario se está leyendo entero. La celda lo recorta a tres
  // líneas y el push a 140 caracteres: sin esta vista, un comentario largo
  // (el formulario deja 1000) no se podía leer completo en ningún lado, justo
  // en la pantalla hecha para enterarse de por qué se va la gente.
  const [detailsRow, setDetailsRow] = useState<DesktopUninstall | null>(null);

  const detailsDate = detailsRow ? parseDate(detailsRow.createdAt) : null;

  const reasonLabel = (row: DesktopUninstall) => {
    if (!row.reason) return t("UNINSTALLS.noReason");

    const key = REASON_KEY[row.reason];

    return key ? t(key) : row.reason;
  };

  const userLabel = (row: DesktopUninstall) =>
    row.userId === null
      ? t("UNINSTALLS.anonymous")
      : (row.email ?? `#${row.userId}`);

  const rows = data?.data ?? [];

  // El total viene del backend: `rows` es un "últimos N" (tope del endpoint)
  // sin paginación, así que contar filas mostraba el tope como si fuera la
  // cantidad de desinstalaciones.
  const total = data?.meta.total ?? 0;

  const isBusy = isLoading || isFetching;

  // Sin esto, un fetch fallido se veía EXACTAMENTE igual que "no desinstaló
  // nadie" — en la única pantalla cuyo propósito es enterarse de lo contrario.
  const failedWithNothingToShow = !isBusy && isError && !data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("UNINSTALLS.title")}
        subtitle={t("UNINSTALLS.subtitle")}
        actions={
          <Badge
            variant="outline"
            className="border-neutral-700 text-neutral-400"
          >
            {t("UNINSTALLS.total")}:{" "}
            <span className="ml-1 font-semibold text-purple-400">
              {data ? total : "—"}
            </span>
          </Badge>
        }
      />

      {isError && (
        <p role="alert" className="text-sm text-red-400">
          {t("UNINSTALLS.error")}
        </p>
      )}

      <Card className="border-neutral-800 bg-neutral-900/50">
        {/* Un solo provider para toda la tabla: uno por celda eran dos por
            fila, hasta el tope de filas del endpoint. La espera de cada
            tooltip queda como con uno por celda: ver TableTooltipProvider. */}
        <TableTooltipProvider>
          <Table>
            <TableHeader>
              <TableRow className="border-neutral-800 hover:bg-transparent">
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colDate")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colInstalledAt")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colUser")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colVersion")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colReason")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colDetails")}
                </TableHead>
                <TableHead className="text-neutral-400">
                  {t("UNINSTALLS.colSystem")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                // Dos fechas distintas: cuándo llegó el aviso (≈ cuándo se
                // desinstaló) y cuándo se había instalado en esa máquina. Las dos
                // pasan por parseDate: `installedAt` es lo que mandó el
                // desinstalador (first-run.json de esa máquina) y una fecha que
                // no se pueda leer no puede tirar la tabla entera.
                const createdAt = parseDate(row.createdAt);

                const installedAt = parseDate(row.installedAt);

                const platformKey = platformLabelKey(row.platform);

                const system = [row.osVersion, row.arch, row.installationType]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <TableRow
                    key={row.id}
                    className="border-neutral-800 hover:bg-neutral-800/50"
                  >
                    <TableCell className="text-sm text-neutral-300">
                      {createdAt ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">
                              {formatDistanceToNow(createdAt, {
                                addSuffix: true,

                                locale: dateLoc,
                              })}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="border-neutral-700 bg-neutral-900 text-neutral-300">
                            {format(createdAt, "dd/MM/yyyy HH:mm")}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Dash />
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-300">
                      {installedAt ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">
                              {format(installedAt, "dd/MM/yyyy")}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="border-neutral-700 bg-neutral-900 text-neutral-300">
                            {format(installedAt, "dd/MM/yyyy HH:mm")}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Dash />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.userId === null ? (
                        <Badge className="bg-neutral-500/20 text-neutral-300">
                          {userLabel(row)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-neutral-100">
                          {userLabel(row)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.appVersion ? (
                        <span className="inline-flex items-center gap-2">
                          <Badge className="bg-violet-500/20 text-violet-400">
                            v{row.appVersion}
                          </Badge>
                          {row.platform && (
                            <span className="text-xs text-neutral-400">
                              {platformKey ? t(platformKey) : row.platform}
                            </span>
                          )}
                        </span>
                      ) : (
                        <Dash />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.reason ? (
                        <Badge className="bg-amber-500/20 text-amber-400">
                          {reasonLabel(row)}
                        </Badge>
                      ) : (
                        <span className="text-xs text-neutral-500">
                          {reasonLabel(row)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.details ? (
                        <div className="flex max-w-md flex-col items-start gap-1">
                          <p
                            id={`uninstall-details-${row.id}`}
                            className="line-clamp-3 whitespace-pre-wrap break-words text-sm text-neutral-300"
                          >
                            {row.details}
                          </p>
                          <button
                            type="button"
                            aria-haspopup="dialog"
                            aria-describedby={`uninstall-details-${row.id}`}
                            onClick={() => setDetailsRow(row)}
                            className="rounded-sm text-xs font-medium text-purple-400 hover:text-purple-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                          >
                            {t("UNINSTALLS.readFull")}
                          </button>
                        </div>
                      ) : (
                        <Dash />
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-neutral-400">
                      {system || <Dash />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableTooltipProvider>

        {isBusy && <LoadingOverlay />}

        {failedWithNothingToShow && (
          <EmptyState
            title={t("UNINSTALLS.error")}
            description={t("UNINSTALLS.errorDesc")}
            className="m-6"
          />
        )}

        {!isBusy && !isError && rows.length === 0 && (
          <EmptyState
            title={t("UNINSTALLS.empty")}
            description={t("UNINSTALLS.emptyDesc")}
            className="m-6"
          />
        )}

        {rows.length > 0 && rows.length < total && (
          <p className="border-t border-neutral-800 px-6 py-3 text-sm text-neutral-400">
            {t("UNINSTALLS.showingOf", { shown: rows.length, total })}
          </p>
        )}
      </Card>

      <Dialog
        open={detailsRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailsRow(null);
        }}
      >
        {detailsRow && (
          <DialogContent className="max-w-2xl border-neutral-800 bg-neutral-900 text-neutral-100">
            <DialogHeader>
              <DialogTitle>{t("UNINSTALLS.detailsTitle")}</DialogTitle>
              <DialogDescription className="text-neutral-400">
                {[
                  userLabel(detailsRow),
                  reasonLabel(detailsRow),
                  detailsDate && format(detailsDate, "dd/MM/yyyy HH:mm"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-800/50 p-4">
              <p
                data-testid="uninstall-details-full"
                className="whitespace-pre-wrap break-words leading-relaxed text-neutral-200"
              >
                {detailsRow.details}
              </p>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default UninstallsPage;
